import React, { useState } from 'react';
import {
    Tooltip,
    OverlayTrigger
} from 'react-bootstrap';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import classNames from 'classnames';

import styles from './styles-label-single-summary.less';

import { ColorPill } from '@graphistry/viz/src/components/color-pill/colorPill';
import { defaultFormat } from '@graphistry/viz/src/formatters';
import { ExpanderIcon } from '@graphistry/viz/src/components/icons';
import { ICON_URIS } from '@graphistry/viz/src/components/collections/constants';
import { SetModal } from '../set-editor/index.ts';

//========================= FRAGMENTS

const filterTooltip = (
  <Tooltip id="filterTooltip">
    Filter this object
  </Tooltip>
);

const excludeTooltip = (
  <Tooltip id="filterTooltip">
    Exclude this object
  </Tooltip>
);

const clipboardTooltip = (
  <Tooltip id="clipboardTooltip">
    Copy to clipboard
  </Tooltip>
);

const filterPropTooltip = (
  <Tooltip id="filterTooltip">
    Filter down to objects with this property
  </Tooltip>
);

const excludePropTooltip = (
  <Tooltip id="filterTooltip">
    Exclude all objects with this property
  </Tooltip>
);

const clipboardPropTooltip = (
  <Tooltip id="clipboardTooltip">
    Copy to clipboard
  </Tooltip>
);

const expandPropTooltip = (
    <Tooltip id="clipboardTooltip">
      Toggle selection expander
    </Tooltip>
  );

const createSetsTooltip = (
  <Tooltip id="createSetsTooltip">
    Create sets
  </Tooltip>
);

const createSetsPropTooltip = (
  <Tooltip id="createSetsPropTooltip">
    Create sets from this property
  </Tooltip>
);

//========================= END FRAGMENTS

//========================= HELPERS

const preventPropagation = f => {
    return function(e) {
        e.stopPropagation();
        return f(e);
    };
};

export const PrimaryInteractions = ({
  children = undefined,
  onExclude,
  onFilter,
  onCreateSets,
  collections,
  toClipboard = undefined,
  interactionDetails,
  toggleExpander = undefined,
  columns = []
}) => {
  console.log({interactionDetails, toggleExpander}, '@PrimaryInteractions');

  const [showSetModal, setShowSetModal] = useState(false);

  return (
    <div className={styles.primaryInteractions}>
      <div className={classNames(styles.buttons, {
        [styles['with-collections']]: collections?.enabled
      })}>
        <OverlayTrigger placement="bottom" overlay={filterTooltip}>
          <button onClick={preventPropagation(() => onFilter(interactionDetails))}>
            <i className="fa fa-fw fa-filter"/>
          </button>
        </OverlayTrigger>

        {onExclude && (
          <OverlayTrigger placement="bottom" overlay={excludeTooltip}>
            <button onClick={preventPropagation(() => onExclude(interactionDetails))}>
              <i className="fa fa-fw fa-ban"/>
            </button>
          </OverlayTrigger>
        )}
        
        {collections?.enabled && onCreateSets && (
          <OverlayTrigger placement="bottom" overlay={createSetsTooltip}>
            <button onClick={preventPropagation(() => setShowSetModal(true))}>
              <img src={ICON_URIS.SET_GRAY} alt="Set icon" style={{ width: '14px', height: '14px' }} />
            </button>
          </OverlayTrigger>
        )}

        {toClipboard === undefined ? (
          <div className={styles.disabled}>
            <i className="fa fa-fw fa-paperclip"/>
          </div>
        ) : (
          <OverlayTrigger placement="bottom" overlay={clipboardTooltip}>
            <CopyToClipboard text={toClipboard} onCopy={() => {}}>
              <button>
                <i className="fa fa-fw fa-paperclip"/>
              </button>
            </CopyToClipboard>
          </OverlayTrigger>
        )}
        
        {toggleExpander && (
          <OverlayTrigger placement="top" overlay={expandPropTooltip}>
            <button onClick={preventPropagation(() => toggleExpander())}>
              <ExpanderIcon/>
            </button>
          </OverlayTrigger>
        )}
      </div>
      {children}
      
      {showSetModal && <SetModal
        isOpen={showSetModal}
        onClose={() => setShowSetModal(false)}
        onConfirm={onCreateSets}
        collections={collections}
        interactionDetails={{
          ...interactionDetails,
          columns
        }}
      />}
    </div>
  );
};

const PropertyInteractions = ({ propertiesEnabled, showActions, componentType, fieldName, fieldValue, fieldType, onFilter, onExclude, onCreateSets, collections, showSetModal, setShowSetModal, columns = [], originalValue }) => {
  const showCreateSets = collections?.enabled && onCreateSets;

  const interactionsClassName = classNames(
    styles['property-interactions'],
    { [styles['with-collections']]: showCreateSets }
  );

  const handleCreateSet = () => {
    setShowSetModal(true);
  };

  return (
    !showActions ? null
    : <div className={interactionsClassName}>
        <button
            onClick={preventPropagation(() => {
                const payload = {
                    componentType,
                    name: fieldName,
                    dataType: fieldType,
                    value: fieldValue
                };

                onExclude(payload);
            })}>
            <OverlayTrigger placement="bottom" overlay={excludePropTooltip} delayShow={300}>
                <i className="fa fa-fw fa-ban"/>
            </OverlayTrigger>
        </button>
        <button
            onClick={preventPropagation(() => {
                const payload = {
                    componentType,
                    name: fieldName,
                    dataType: fieldType,
                    value: fieldValue
                };

                onFilter(payload);
            })}>
            <OverlayTrigger placement="bottom" overlay={filterPropTooltip} delayShow={300}>
                <i className="fa fa-fw fa-filter"/>
            </OverlayTrigger>
        </button>
        {showCreateSets && (
          <button onClick={preventPropagation(handleCreateSet)}>
              <OverlayTrigger placement="bottom" overlay={createSetsPropTooltip} delayShow={300}>
                  <img src={ICON_URIS.SET_LIGHT} alt="Set icon" style={{ width: '14px', height: '14px' }} />
              </OverlayTrigger>
          </button>
        )}
        <CopyToClipboard text={fieldValue}>
            <button>
                <OverlayTrigger placement="bottom" overlay={clipboardPropTooltip} delayShow={300}>
                    <i className="fa fa-fw fa-paperclip"/>
                </OverlayTrigger>
            </button>
        </CopyToClipboard>

        {showSetModal && <SetModal
          isOpen={showSetModal}
          onClose={() => setShowSetModal(false)}
          onConfirm={onCreateSets}
          collections={collections}
          interactionDetails={{
            type: 'gfql_chain',
            componentType,
            name: fieldName,
            dataType: fieldType,
            value: originalValue !== undefined ? originalValue : fieldValue,
            columns
          }}
        />}
    </div>
  );
};

const Property = ({ propertiesEnabled, componentType, fieldName, fieldValue, fieldType, onFilter, onExclude, showActions, onCreateSets, collections, columns, originalValue }) => {
    const [showPropertySetModal, setShowPropertySetModal] = useState(false);
    
    const propertyInteractions = (
        <PropertyInteractions
            onFilter={onFilter}
            onExclude={onExclude}
            onCreateSets={onCreateSets}
            fieldType={fieldType}
            fieldName={fieldName}
            fieldValue={fieldValue}
            originalValue={originalValue}
            componentType={componentType}
            propertiesEnabled={propertiesEnabled}
            showActions={showActions}
            collections={collections}
            showSetModal={showPropertySetModal}
            setShowSetModal={setShowPropertySetModal}
            columns={columns}
        />
    );

    return (
        <li className={`${styles.property} ${styles['properties-enabled-' + (propertiesEnabled === false ? 'off' : 'on')]}`}>
            {propertiesEnabled === false ? propertyInteractions : undefined }
            <div className={styles['property-name']}>{fieldName.replace(/^_grouped_/,'')}</div>
            <div className={styles['property-value']}>
                {fieldType === 'color'
                    ? <span><ColorPill color={fieldValue} /> {fieldValue}</span>
                    : <span dangerouslySetInnerHTML={{ __html: fieldValue }} />
                }
                {propertiesEnabled === false ? undefined : propertyInteractions}
            </div>
        </li>
    );
}

const PropertyList = ({ propertiesEnabled, showActions, onFilter, onExclude, onCreateSets, componentType, properties = [], collections, columns = [] }) => (
    <ul className={styles.propertyList}>
        {properties.map(p => {
            const fieldValue = p.displayName || defaultFormat(p.value, p.dataType);
            return fieldValue === null || fieldValue === undefined ? null : (
                <Property
                    key={`${p.key}-${p.value}`}
                    componentType={componentType}
                    fieldName={p.key}
                    fieldValue={fieldValue}
                    fieldType={p.dataType}
                    onFilter={onFilter}
                    onExclude={onExclude}
                    onCreateSets={onCreateSets}
                    propertiesEnabled={propertiesEnabled}
                    showActions={showActions}
                    collections={collections}
                    columns={columns}
                    originalValue={p.value}
                />
            );
        })}
    </ul>
);

export const LabelSingleSummary = ({
    onExclude, 
    onFilter, 
    propertiesEnabled, 
    showActions,
    onCreateSets,
    collections,

    //Label.fragment
    type, 
    importantColumns = [], 
    columns = [],
    style, 
    className,
 }) => {
    const containerClassName = classNames(
        className,
        styles.yScrollContainer,
        { [styles['with-collections']]: collections?.enabled }
    );

    return (
        <div style={style} className={containerClassName}>
            {importantColumns.length > 0
                ? <PropertyList
                    key='important-columns'
                    componentType={type}
                    onFilter={onFilter}
                    onExclude={onExclude}
                    onCreateSets={onCreateSets}
                    collections={collections}
                    properties={importantColumns}
                    propertiesEnabled={propertiesEnabled}
                    showActions={showActions}
                    columns={columns}
                />
                : null }
            {importantColumns.length !== columns.length && importantColumns.length
                ? <div className={styles.propertyTitle}>All Properties</div>
                : null }
            {importantColumns.length !== columns.length
                ? <PropertyList
                    key='all-columns'
                    componentType={type}
                    properties={columns}
                    onFilter={onFilter}
                    onExclude={onExclude}
                    onCreateSets={onCreateSets}
                    collections={collections}
                    propertiesEnabled={propertiesEnabled}
                    showActions={showActions}
                    columns={columns}
                />
                : null }
        </div>
    );
};
